<script>
import RawData from "@/components/widgets/raw-data.vue";
import { cloneDeep } from "lodash";

export default {
  name: "StockList",
  components: {
    RawData
  },
  props: {
    stockList: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      localStockList: [],
      filter: "",
      currentPage: 1,
      perPage: 10,
      pageOptions: [10, 25, 50, 100],
      sortBy: "stockNumber",
      sortDesc: false,
      fields: [
        {
          key: "select",
          label: "",
          thStyle: { width: "40px" },
          tdClass: "text-center align-middle",
          thClass: "text-center align-middle"
        },
        {
          key: "stockNumber",
          label: "Stock No.",
          sortable: true,
          thStyle: { width: "180px" },
          tdClass: "align-middle"
        },
        {
          key: "name",
          label: "Item Name & Description",
          sortable: true,
          tdClass: "align-middle"
        },
        {
          key: "unit",
          label: "Unit",
          sortable: true,
          thStyle: { width: "100px" },
          tdClass: "text-center align-middle",
          thClass: "text-center align-middle"
        },
        {
          key: "unitCost",
          label: "Unit Cost",
          sortable: true,
          thStyle: { width: "140px" },
          tdClass: "text-end align-middle",
          thClass: "text-end align-middle"
        },
        {
          key: "available",
          label: "Current Balance",
          sortable: true,
          thStyle: { width: "150px" },
          tdClass: "text-center align-middle",
          thClass: "text-center align-middle"
        }
      ]
    };
  },
  computed: {
    filteredStockList() {
      if (!this.filter || !this.filter.trim()) {
        return this.localStockList;
      }
      const term = this.filter.trim().toUpperCase();
      return this.localStockList.filter((item) => {
        const stockNo = String(item.stockNumber || "").toUpperCase();
        const name = String(item.name || item.itemName || "").toUpperCase();
        const desc = String(item.description || "").toUpperCase();
        const unit = String(item.unit || "").toUpperCase();
        return (
          stockNo.includes(term) ||
          name.includes(term) ||
          desc.includes(term) ||
          unit.includes(term)
        );
      });
    },
    totalRows() {
      return this.filteredStockList.length;
    },
    selectedItems() {
      return this.localStockList.filter((item) => item.selected);
    },
    selectedCount() {
      return this.selectedItems.length;
    },
    isAllSelected() {
      if (!this.filteredStockList.length) return false;
      return this.filteredStockList.every((item) => item.selected);
    }
  },
  watch: {
    stockList: {
      immediate: true,
      handler(newVal) {
        this.initStockList(newVal || []);
      }
    }
  },
  methods: {
    initStockList(list) {
      this.localStockList = list.map((item) => ({
        ...cloneDeep(item),
        selected: item.selected || false
      }));
    },
    toggleSelectAll() {
      const willSelect = !this.isAllSelected;
      const filteredIds = new Set(this.filteredStockList.map((item) => item.id || item.stockItemId));
      this.localStockList.forEach((item) => {
        const id = item.id || item.stockItemId;
        if (filteredIds.has(id)) {
          this.$set(item, "selected", willSelect);
        }
      });
    },
    onItemSelect() {
      // Triggers reactivity
    },
    handleCreateRequest() {
      if (this.selectedCount === 0) {
        this.showToast("Please select at least one stock item.", "warning");
        return;
      }
      const selected = cloneDeep(this.selectedItems);
      this.$emit("create-request", selected);
      this.$emit("createRequest", selected);
    },
    formatCurrency(val) {
      const amount = parseFloat(val);
      if (isNaN(amount)) return "₱0.00";
      return new Intl.NumberFormat("en-PH", {
        style: "currency",
        currency: "PHP"
      }).format(amount);
    },
    getAvailableQty(item) {
      if (item.balance !== undefined && item.balance !== null) return item.balance;
      return 0;
    }
  }
};
</script>

<template>
  <div>
    <!-- Table Controls / Header Action Bar -->
    <div class="row align-items-center mb-3">
      <div class="col-sm-12 col-md-6 d-flex align-items-center flex-wrap gap-2 mb-2 mb-md-0">
        <div id="tickets-table_length" class="dataTables_length">
          <label class="d-inline-flex align-items-center mb-0">
            Show&nbsp;
            <b-form-select class="form-select form-select-sm mx-1" v-model="perPage" size="sm"
              :options="pageOptions"></b-form-select>
            &nbsp;entries
          </label>
        </div>

        <!-- Create Request Button with count -->
        <button type="button" class="btn btn-sm btn-primary d-flex align-items-center ms-md-2"
          :disabled="selectedCount === 0" @click="handleCreateRequest">
          <i class="bx bx-plus font-size-16 me-1"></i>
          Create Request
          <span v-if="selectedCount > 0" class="badge bg-light text-primary ms-1">
            {{ selectedCount }}
          </span>
        </button>
      </div>

      <!-- Search Input -->
      <div class="col-sm-12 col-md-6">
        <div class="dataTables_filter text-md-end">
          <label class="d-inline-flex align-items-center mb-0">
            Search:
            <b-form-input v-model="filter" type="search" placeholder="Search stocks..."
              class="form-control form-control-sm ms-2"></b-form-input>
          </label>
        </div>
      </div>
    </div>

    <!-- Table Container -->
    <div class="custom-table-responsive table-responsive">
      <b-table class="table project-list-table align-middle table-borderless table-hover" :items="filteredStockList"
        :fields="fields" :per-page="perPage" :current-page="currentPage" :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc" :busy="loading" responsive show-empty>
        <!-- Loading State -->
        <template #table-busy>
          <div class="d-flex justify-content-center align-items-center py-4">
            <div class="preloader-component me-2">
              <div class="status">
                <div class="spinner-chase w-20px h-20px">
                  <div class="chase-dot"></div>
                  <div class="chase-dot"></div>
                  <div class="chase-dot"></div>
                  <div class="chase-dot"></div>
                  <div class="chase-dot"></div>
                  <div class="chase-dot"></div>
                </div>
              </div>
            </div>
            <strong>Loading stocks...</strong>
          </div>
        </template>

        <!-- Empty State -->
        <template #empty>
          <div class="text-center py-4">
            <i class="fas fa-exclamation-triangle text-warning font-size-24 mb-2"></i>
            <h5 class="text-secondary mb-0">
              {{ filter ? 'No matching stocks found.' : 'No stock items available.' }}
            </h5>
          </div>
        </template>

        <!-- Select All Checkbox Header -->
        <template #head(select)>
          <div class="form-check form-checkbox-outline form-check-success mb-0 d-flex justify-content-center">
            <input type="checkbox" class="form-check-input cursor-pointer" :checked="isAllSelected"
              @change="toggleSelectAll" title="Select All" />
          </div>
        </template>

        <!-- Row Checkbox Cell -->
        <template #cell(select)="data">
          <div class="form-check form-checkbox-outline form-check-success mb-0 d-flex justify-content-center">
            <input type="checkbox" class="form-check-input cursor-pointer" v-model="data.item.selected"
              @change="onItemSelect" />
          </div>
        </template>

        <!-- Stock Number Cell -->
        <template #cell(stockNumber)="data">
          <div class="position-relative">
            <RawData :data="data.item" :keyId="`stock-${data.item.id || data.index}`"
              v-if="$store.state.decrypt.isAdmin()" />
            <span class="text-primary fw-medium">{{ data.value || 'N/A' }}</span>
          </div>
        </template>

        <!-- Name & Description Cell -->
        <template #cell(name)="data">
          <div>
            <h6 class="font-size-14 mb-0 text-truncate text-body">
              {{ data.item.name || data.item.itemName || 'Unnamed Item' }}
            </h6>
            <small v-if="data.item.description" class="text-muted d-block font-size-12">
              <i>{{ data.item.description }}</i>
            </small>
          </div>
        </template>

        <!-- Unit Cell -->
        <template #cell(unit)="data">
          <span class="text-muted font-size-13">{{ data.value || 'unit' }}</span>
        </template>

        <!-- Unit Cost Cell -->
        <template #cell(unitCost)="data">
          <span class="text-warning font-size-13 fw-semibold">
            {{ formatCurrency(data.value) }}
          </span>
        </template>

        <!-- Available Balance Cell -->
        <template #cell(available)="data">
          <span class="badge font-size-13 py-1 px-2"
            :class="getAvailableQty(data.item) > 0 ? 'bg-success' : 'bg-danger'">
            {{ getAvailableQty(data.item) }}
          </span>
        </template>
      </b-table>
    </div>

    <!-- Pagination -->
    <div class="row align-items-center mt-2" v-if="totalRows > 0">
      <div class="col-sm-12 col-md-6">
        <p class="text-muted font-size-13 mb-0">
          Showing {{ ((currentPage - 1) * perPage) + 1 }} to {{ Math.min(currentPage * perPage, totalRows) }} of {{
            totalRows }} entries
        </p>
      </div>
      <div class="col-sm-12 col-md-6">
        <div class="dataTables_paginate paging_simple_numbers float-md-end">
          <ul class="pagination pagination-rounded mb-0">
            <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage"></b-pagination>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>